import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { ArrowLeft, Plus, Trash2, Save, KeyRound, Mail, Ban, RotateCcw } from 'lucide-react'
import type {
  UserResponse,
  TenantResponse,
  PaginatedResponse,
  AssignmentResponse,
  RoleResponse,
  ProductResponse,
  BookmakerResponse,
  AuditLogResponse,
  TimezoneResponse,
} from '@/api/types'

export function UserDetailPage() {
  const { t } = useTranslation(['users', 'common', 'audit'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user: me } = useAuth()
  const userId = Number(id)

  // --- User data ---
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api.get<UserResponse>(`/users/${userId}`),
    enabled: !!userId,
  })
  const user = userData?.data

  // --- Edit state ---
  const [editFields, setEditFields] = useState<Record<string, string | null>>({})

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, string | undefined>) => api.put(`/users/${userId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditFields({})
    },
  })

  function getField(key: string, original: string | undefined | null): string {
    if (editFields[key] !== undefined && editFields[key] !== null) return editFields[key]!
    return original ?? ''
  }

  function setField(key: string, value: string) {
    setEditFields((prev) => ({ ...prev, [key]: value }))
  }

  // --- Tenants ---
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?offset=0&limit=100'),
    enabled: me?.platform_role === 'PLATFORM_ADMIN',
  })
  const allTenants = tenantsData?.data.data ?? []

  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<number | ''>('')

  const addTenantMutation = useMutation({
    mutationFn: (tenantId: number) =>
      api.post(`/tenants/${tenantId}/users`, { user_id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      setAddTenantDialogOpen(false)
      setSelectedTenantId('')
    },
  })

  const removeTenantMutation = useMutation({
    mutationFn: (tenantId: number) =>
      api.delete(`/tenants/${tenantId}/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
    },
  })

  // --- Assignments ---
  const { data: assignmentsData } = useQuery({
    queryKey: ['assignments', 'user', userId],
    queryFn: () => api.get<PaginatedResponse<AssignmentResponse>>(`/assignments?user_id=${userId}&limit=100`),
    enabled: !!userId,
  })
  const assignments = assignmentsData?.data.data ?? []

  const { data: rolesData } = useQuery({
    queryKey: ['roles-all'],
    queryFn: () => api.get<PaginatedResponse<RoleResponse>>('/roles?offset=0&limit=100'),
  })
  const allRoles = rolesData?.data.data ?? []

  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>('/products?offset=0&limit=100'),
  })
  const allProducts = productsData?.data.data ?? []

  const { data: bookmakersData } = useQuery({
    queryKey: ['bookmakers-all'],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>('/bookmakers?offset=0&limit=500'),
  })
  const allBookmakers = bookmakersData?.data.data ?? []

  const { data: tzData } = useQuery({
    queryKey: ['timezones'],
    queryFn: () => api.get<TimezoneResponse[]>('/timezones'),
  })
  const allTimezones = tzData?.data ?? []

  // --- Audit logs ---
  // Actions performed BY this user
  const { data: auditByUserData } = useQuery({
    queryKey: ['audit', 'actor', userId],
    queryFn: () => api.get<{ data: AuditLogResponse[]; total: number }>(`/audit?actor_id=${userId}&limit=100`),
    enabled: !!userId,
  })
  // Actions performed ON this user
  const { data: auditOnUserData } = useQuery({
    queryKey: ['audit', 'entity', 'user', userId],
    queryFn: () => api.get<{ data: AuditLogResponse[]; total: number }>(`/audit?entity_type=user&entity_id=${userId}&limit=100`),
    enabled: !!userId,
  })

  const auditLogs = (() => {
    const byUser = (auditByUserData?.data.data ?? []).map((l) => ({ ...l, _source: 'actor' as const }))
    const onUser = (auditOnUserData?.data.data ?? []).map((l) => ({ ...l, _source: 'target' as const }))
    // Merge, deduplicate by id, sort by date desc.
    // The map holds entries from either list, so its value type has to be the
    // union: byUser alone pins _source to the literal 'actor'.
    const map = new Map<number, typeof byUser[number] | typeof onUser[number]>()
    for (const l of byUser) map.set(l.id, l)
    for (const l of onUser) if (!map.has(l.id)) map.set(l.id, { ...l, _source: 'target' })
    return [...map.values()].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  })()

  const [auditFilter, setAuditFilter] = useState<'all' | 'actor' | 'target'>('all')
  const filteredAuditLogs = auditFilter === 'all' ? auditLogs : auditLogs.filter((l) => l._source === auditFilter)

  // --- Action dialogs ---
  const [changePassOpen, setChangePassOpen] = useState(false)
  const [resetPassOpen, setResetPassOpen] = useState(false)
  const [suspendOpen, setSuspendOpen] = useState(false)
  const [reactivateOpen, setReactivateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [actionError, setActionError] = useState('')
  const [actionSuccess, setActionSuccess] = useState('')

  const changePasswordMutation = useMutation({
    mutationFn: (password: string) => api.post(`/users/${userId}/change-password`, { new_password: password }),
    onSuccess: () => {
      setChangePassOpen(false)
      setNewPassword('')
      setActionError('')
      setActionSuccess(t('actions.passwordChanged'))
      setTimeout(() => setActionSuccess(''), 3000)
    },
    onError: () => setActionError(t('actions.changePasswordError')),
  })

  const resetPasswordMutation = useMutation({
    mutationFn: () => api.post(`/users/${userId}/reset-password`, {}),
    onSuccess: () => {
      setResetPassOpen(false)
      setActionSuccess(t('actions.resetEmailSent'))
      setTimeout(() => setActionSuccess(''), 3000)
    },
    onError: () => setActionError(t('actions.resetEmailError')),
  })

  const suspendMutation = useMutation({
    mutationFn: () => api.post(`/users/${userId}/suspend`, {}),
    onSuccess: () => {
      setSuspendOpen(false)
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setActionSuccess(t('actions.accountSuspended'))
      setTimeout(() => setActionSuccess(''), 3000)
    },
    onError: () => setActionError(t('actions.suspendError')),
  })

  const reactivateMutation = useMutation({
    mutationFn: () => api.post(`/users/${userId}/reactivate`, {}),
    onSuccess: () => {
      setReactivateOpen(false)
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setActionSuccess(t('actions.accountReactivated'))
      setTimeout(() => setActionSuccess(''), 3000)
    },
    onError: () => setActionError(t('actions.reactivateError')),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      navigate('/users')
    },
    onError: () => setActionError(t('actions.deleteError')),
  })

  const [addAssignDialogOpen, setAddAssignDialogOpen] = useState(false)
  const [assignTenantId, setAssignTenantId] = useState<string>('')
  const [assignRoleId, setAssignRoleId] = useState<string>('')
  const [assignBookmakerId, setAssignBookmakerId] = useState<string>('')

  // Fetch bookmakers for the selected tenant in assignment form
  const { data: assignBookmakersData } = useQuery({
    queryKey: ['bookmakers-by-tenant', assignTenantId],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>(`/tenants/${assignTenantId}/bookmakers?offset=0&limit=100`),
    enabled: !!assignTenantId,
  })
  const assignSites = assignBookmakersData?.data.data ?? []

  function resetAssignForm() {
    setAssignTenantId('')
    setAssignRoleId('')
    setAssignBookmakerId('')
  }

  const createAssignmentMutation = useMutation({
    mutationFn: () =>
      api.post('/assignments', {
        user_id: userId,
        role_id: Number(assignRoleId),
        tenant_id: Number(assignTenantId),
        ...(assignBookmakerId ? { bookmaker_id: Number(assignBookmakerId) } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments', 'user', userId] })
      setAddAssignDialogOpen(false)
      resetAssignForm()
    },
  })

  const deleteAssignmentMutation = useMutation({
    mutationFn: (assignId: number) => api.delete(`/assignments/${assignId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assignments', 'user', userId] }),
  })

  if (isLoading) return <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
  if (!user) return <p className="text-destructive">{t('notFound')}</p>

  const userTenantIds = user.tenant_ids ?? []
  const availableTenants = allTenants.filter((tn) => !userTenantIds.includes(tn.id))
  const roleLookupObj = Object.fromEntries(allRoles.map((r) => [r.id, r]))
  const roleLookup = Object.fromEntries(allRoles.map((r) => [r.id, r.name]))
  const productLookup = Object.fromEntries(allProducts.map((p) => [p.id, p.name]))
  const siteLookup = Object.fromEntries(allBookmakers.map((s) => [s.id, s.name]))

  function handleSave() {
    const body: Record<string, string | undefined> = {}
    const fields: [string, string | undefined | null][] = [
      ['username', user!.username],
      ['email', user!.email],
      ['status', user!.status],
      ['first_name', user!.first_name],
      ['last_name', user!.last_name],
      ['gender', user!.gender],
      ['birth_date', user!.birth_date],
      ['country_code', user!.country_code],
      ['pref_language', user!.pref_language],
      ['pref_timezone', user!.pref_timezone],
    ]
    for (const [key, original] of fields) {
      if (editFields[key] !== undefined && editFields[key] !== null && editFields[key] !== (original ?? '')) {
        body[key] = editFields[key]!
      }
    }
    if (Object.keys(body).length > 0) updateMutation.mutate(body)
  }

  const isEditing = Object.values(editFields).some((v) => v !== undefined && v !== null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-4 w-4 mr-1" />{t('buttons.back', { ns: 'common' })}
        </Button>
        <h2 className="text-2xl font-semibold">{t('detail.title', { id: user.id })}</h2>
        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>{user.status}</Badge>
      </div>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('detail.account')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('labels.username', { ns: 'common' })}</Label>
              <Input value={getField('username', user.username)} onChange={(e) => setField('username', e.target.value)} />
            </div>
            <div>
              <Label>{t('labels.email', { ns: 'common' })}</Label>
              <Input value={getField('email', user.email)} onChange={(e) => setField('email', e.target.value)} />
            </div>
            <div>
              <Label>{t('labels.status', { ns: 'common' })}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={getField('status', user.status)}
                onChange={(e) => setField('status', e.target.value)}
              >
                <option value="active">{t('statuses.active', { ns: 'common' })}</option>
                <option value="suspended">{t('statuses.suspended', { ns: 'common' })}</option>
                <option value="inactive">{t('statuses.inactive', { ns: 'common' })}</option>
                <option value="deleted">{t('statuses.deleted', { ns: 'common' })}</option>
              </select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {t('labels.created', { ns: 'common' })}: {new Date(user.created_at).toLocaleString()} | {t('labels.updated', { ns: 'common' })}: {new Date(user.updated_at).toLocaleString()}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('labels.actions', { ns: 'common' })}</CardTitle>
        </CardHeader>
        <CardContent>
          {actionSuccess && (
            <div className="bg-green-100 text-green-800 text-sm p-3 rounded-md mb-4">{actionSuccess}</div>
          )}
          {actionError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4">{actionError}</div>
          )}
          <div className="flex gap-3 flex-wrap">
            {/* Change Password */}
            <Dialog open={changePassOpen} onOpenChange={(open) => { setChangePassOpen(open); if (!open) { setNewPassword(''); setActionError('') } }}>
              <DialogTrigger render={<Button variant="outline" size="sm" />}>
                <KeyRound className="h-4 w-4 mr-2" />{t('actions.changePassword')}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('actions.changePassword')}</DialogTitle></DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); changePasswordMutation.mutate(newPassword) }} className="flex flex-col gap-4">
                  <div>
                    <Label htmlFor="new-password">{t('actions.newPassword')}</Label>
                    <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
                  </div>
                  <Button type="submit" disabled={changePasswordMutation.isPending}>
                    {changePasswordMutation.isPending ? t('actions.changing') : t('actions.changePassword')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Reset Password */}
            <Dialog open={resetPassOpen} onOpenChange={(open) => { setResetPassOpen(open); if (!open) setActionError('') }}>
              <DialogTrigger render={<Button variant="outline" size="sm" />}>
                <Mail className="h-4 w-4 mr-2" />{t('actions.resetPassword')}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('actions.resetPassword')}</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">
                  {t('actions.resetPasswordConfirm', { email: user.email })}
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setResetPassOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
                  <Button
                    disabled={resetPasswordMutation.isPending}
                    onClick={() => { setActionError(''); resetPasswordMutation.mutate() }}
                  >
                    {resetPasswordMutation.isPending ? t('actions.sending') : t('actions.sendResetEmail')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Suspend / Reactivate */}
            {user.status === 'suspended' ? (
              <Dialog open={reactivateOpen} onOpenChange={(open) => { setReactivateOpen(open); if (!open) setActionError('') }}>
                <DialogTrigger render={<Button variant="outline" size="sm" />}>
                  <RotateCcw className="h-4 w-4 mr-2" />{t('actions.reactivateAccount')}
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('actions.reactivateAccount')}</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    {t('actions.reactivateConfirm', { email: user.email })}
                  </p>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setReactivateOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
                    <Button
                      disabled={reactivateMutation.isPending}
                      onClick={() => { setActionError(''); reactivateMutation.mutate() }}
                    >
                      {reactivateMutation.isPending ? t('actions.reactivating') : t('actions.confirmReactivation')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog open={suspendOpen} onOpenChange={(open) => { setSuspendOpen(open); if (!open) setActionError('') }}>
                <DialogTrigger render={<Button variant="destructive" size="sm" disabled={user.status === 'inactive'} />}>
                  <Ban className="h-4 w-4 mr-2" />{t('actions.suspendAccount')}
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('actions.suspendAccount')}</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    {t('actions.suspendConfirm', { email: user.email })}
                  </p>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setSuspendOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
                    <Button
                      variant="destructive"
                      disabled={suspendMutation.isPending}
                      onClick={() => { setActionError(''); suspendMutation.mutate() }}
                    >
                      {suspendMutation.isPending ? t('actions.suspending') : t('actions.confirmSuspension')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Delete */}
            <Dialog open={deleteOpen} onOpenChange={(open) => { setDeleteOpen(open); if (!open) setActionError('') }}>
              <DialogTrigger render={<Button variant="destructive" size="sm" />}>
                <Trash2 className="h-4 w-4 mr-2" />{t('actions.deleteUser')}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('actions.deleteUser')}</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground">
                  {t('actions.deleteConfirm', { name: user.username ?? user.email })}
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
                  <Button
                    variant="destructive"
                    disabled={deleteMutation.isPending}
                    onClick={() => { setActionError(''); deleteMutation.mutate() }}
                  >
                    {deleteMutation.isPending ? t('actions.deleting') : t('actions.confirmDeletion')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Personal Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('detail.personalInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('labels.firstName', { ns: 'common' })}</Label>
              <Input value={getField('first_name', user.first_name)} onChange={(e) => setField('first_name', e.target.value)} />
            </div>
            <div>
              <Label>{t('labels.lastName', { ns: 'common' })}</Label>
              <Input value={getField('last_name', user.last_name)} onChange={(e) => setField('last_name', e.target.value)} />
            </div>
            <div>
              <Label>{t('labels.gender', { ns: 'common' })}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={getField('gender', user.gender)}
                onChange={(e) => setField('gender', e.target.value)}
              >
                <option value="">—</option>
                <option value="male">{t('genders.male', { ns: 'common' })}</option>
                <option value="female">{t('genders.female', { ns: 'common' })}</option>
                <option value="other">{t('genders.other', { ns: 'common' })}</option>
              </select>
            </div>
            <div>
              <Label>{t('labels.birthDate', { ns: 'common' })}</Label>
              <Input type="date" value={getField('birth_date', user.birth_date)} onChange={(e) => setField('birth_date', e.target.value)} />
            </div>
            <div>
              <Label>{t('labels.countryCode', { ns: 'common' })}</Label>
              <Input value={getField('country_code', user.country_code)} onChange={(e) => setField('country_code', e.target.value)} placeholder="e.g. IT" maxLength={3} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('labels.prefLanguage', { ns: 'common' })}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={getField('pref_language', user.pref_language)}
                onChange={(e) => setField('pref_language', e.target.value)}
              >
                <option value="en">English</option>
                <option value="it">Italiano</option>
                <option value="es">Español</option>
                <option value="pt">Português</option>
              </select>
            </div>
            <div>
              <Label>{t('labels.prefTimezone', { ns: 'common' })}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={getField('pref_timezone', user.pref_timezone)}
                onChange={(e) => setField('pref_timezone', e.target.value)}
              >
                {allTimezones.map(tz => (
                  <option key={tz.id} value={tz.name}>({tz.utc_offset}) {tz.name}</option>
                ))}
              </select>
            </div>
          </div>
          {isEditing && (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                <Save className="h-4 w-4 mr-1" />{t('buttons.save', { ns: 'common' })}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditFields({})}>
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Groups */}
      {user.groups && user.groups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('detail.groups')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {user.groups.map(g => (
                <Badge key={g.id} variant="secondary">{g.name}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tenant Associations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t('detail.tenants')}</CardTitle>
          {me?.platform_role === 'PLATFORM_ADMIN' && (
            <Dialog open={addTenantDialogOpen} onOpenChange={setAddTenantDialogOpen}>
              <DialogTrigger render={<Button size="sm" />}>
                <Plus className="h-4 w-4 mr-1" />{t('detail.addTenant')}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('detail.addTenantAssociation')}</DialogTitle></DialogHeader>
                <div className="flex flex-col gap-4">
                  <div>
                    <Label>{t('labels.tenant', { ns: 'common' })}</Label>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                      value={selectedTenantId}
                      onChange={(e) => setSelectedTenantId(e.target.value ? Number(e.target.value) : '')}
                    >
                      <option value="">{t('detail.selectTenant')}</option>
                      {availableTenants.map((tn) => (
                        <option key={tn.id} value={tn.id}>{tn.name} (ID: {tn.id})</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={() => selectedTenantId !== '' && addTenantMutation.mutate(selectedTenantId)}
                    disabled={selectedTenantId === '' || addTenantMutation.isPending}
                  >
                    {t('buttons.add', { ns: 'common' })}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {userTenantIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('detail.noTenantAssociations')}</p>
          ) : (
            <div className="border rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('labels.id', { ns: 'common' })}</th>
                    <th className="text-left p-3 font-medium">{t('labels.name', { ns: 'common' })}</th>
                    {me?.platform_role === 'PLATFORM_ADMIN' && <th className="p-3 w-12"></th>}
                  </tr>
                </thead>
                <tbody>
                  {userTenantIds.map((tid) => {
                    const tn = allTenants.find((x) => x.id === tid)
                    return (
                      <tr key={tid} className="border-b last:border-0">
                        <td className="p-3 font-mono text-xs">{tid}</td>
                        <td className="p-3">{tn?.name ?? '—'}</td>
                        {me?.platform_role === 'PLATFORM_ADMIN' && (
                          <td className="p-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTenantMutation.mutate(tid)}
                              disabled={removeTenantMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Roles & Assignments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t('detail.rolesAndAssignments')}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('detail.rolesDescription')}
            </p>
          </div>
          <Dialog open={addAssignDialogOpen} onOpenChange={(open) => { setAddAssignDialogOpen(open); if (!open) resetAssignForm() }}>
            <DialogTrigger render={<Button size="sm" />}>
              <Plus className="h-4 w-4 mr-1" />{t('detail.addRole')}
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('detail.assignRole')}</DialogTitle></DialogHeader>
              <form
                onSubmit={(e) => { e.preventDefault(); createAssignmentMutation.mutate() }}
                className="flex flex-col gap-4"
              >
                {/* Step 1: Tenant */}
                <div>
                  <Label>{t('labels.tenant', { ns: 'common' })}</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={assignTenantId}
                    onChange={(e) => { setAssignTenantId(e.target.value); setAssignRoleId(''); setAssignBookmakerId('') }}
                    required
                  >
                    <option value="">{t('detail.selectTenant')}</option>
                    {userTenantIds.map((tid) => {
                      const tn = allTenants.find((x) => x.id === tid)
                      return <option key={tid} value={tid}>{tn?.name ?? `Tenant ${tid}`}</option>
                    })}
                  </select>
                </div>

                {/* Step 2: Role (visible after tenant) */}
                {assignTenantId && (
                  <div>
                    <Label>{t('labels.role', { ns: 'common' })}</Label>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                      value={assignRoleId}
                      onChange={(e) => setAssignRoleId(e.target.value)}
                      required
                    >
                      <option value="">{t('placeholders.selectRole', { ns: 'common' })}</option>
                      {allRoles.map((r) => {
                        const prodName = r.product_id ? productLookup[r.product_id] : null
                        const label = prodName ? `${r.name} (${r.scope_level} — ${prodName})` : `${r.name} (${r.scope_level})`
                        return <option key={r.id} value={r.id}>{label}</option>
                      })}
                    </select>
                  </div>
                )}

                {/* Step 3: Bookmaker (visible after role, optional) */}
                {assignRoleId && (
                  <div>
                    <Label>{t('detail.bookmakerLabel')}</Label>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                      value={assignBookmakerId}
                      onChange={(e) => setAssignBookmakerId(e.target.value)}
                    >
                      <option value="">{t('labels.allBookmakers', { ns: 'common' })}</option>
                      {assignSites.map((s) => (
                        <option key={s.id} value={s.id}>{s.name} ({s.domain})</option>
                      ))}
                    </select>
                  </div>
                )}

                <Button type="submit" disabled={!assignTenantId || !assignRoleId || createAssignmentMutation.isPending}>
                  {t('detail.assignRole')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('detail.noRolesAssigned')}</p>
          ) : (
            <>
              {/* Role badges summary */}
              <div className="flex gap-2 flex-wrap mb-4">
                {[...new Set(assignments.map((a) => a.role_id))].map((rid) => (
                  <Badge key={rid} variant="default">{roleLookup[rid] ?? `Role #${rid}`}</Badge>
                ))}
              </div>
              {/* Detailed assignments table */}
              <div className="border rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">{t('labels.role', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.product', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.tenant', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.bookmaker', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.assigned', { ns: 'common' })}</th>
                      <th className="p-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => (
                      <tr key={a.id} className="border-b last:border-0">
                        <td className="p-3 font-medium">{roleLookup[a.role_id] ?? a.role_id}</td>
                        <td className="p-3">{(() => { const r = roleLookupObj[a.role_id]; return r?.product_id ? (productLookup[r.product_id] ?? `#${r.product_id}`) : '—' })()}</td>
                        <td className="p-3">{allTenants.find((tn) => tn.id === a.tenant_id)?.name ?? a.tenant_id}</td>
                        <td className="p-3">{a.bookmaker_id ? (siteLookup[a.bookmaker_id] ?? a.bookmaker_id) : t('detail.all')}</td>
                        <td className="p-3 text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAssignmentMutation.mutate(a.id)}
                            disabled={deleteAssignmentMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t('title', { ns: 'audit' })}</CardTitle>
          <div className="flex gap-1">
            {(['all', 'actor', 'target'] as const).map((f) => (
              <Button
                key={f}
                variant={auditFilter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAuditFilter(f)}
              >
                {f === 'all' ? t('filterAll', { ns: 'audit' }) : f === 'actor' ? t('filterByUser', { ns: 'audit' }) : t('filterOnUser', { ns: 'audit' })}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {filteredAuditLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('empty', { ns: 'audit' })}</p>
          ) : (
            <div className="border rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('labels.date', { ns: 'common' })}</th>
                    <th className="text-left p-3 font-medium">{t('labels.action', { ns: 'common' })}</th>
                    <th className="text-left p-3 font-medium">{t('fields.actor', { ns: 'audit' })}</th>
                    <th className="text-left p-3 font-medium">{t('labels.details', { ns: 'common' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAuditLogs.map((log) => (
                    <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/audit/${log.id}`)}>
                      <td className="p-3 text-muted-foreground whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                      <td className="p-3"><Badge variant="secondary">{log.action}</Badge></td>
                      <td className="p-3">{log.actor_email ?? (log.actor_id ? `User #${log.actor_id}` : '—')}</td>
                      <td className="p-3 text-muted-foreground">{log.details ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
